import React, { useState, useEffect, useCallback } from "react";
import Modal from "react-modal";
import { MdClose } from "react-icons/md";
import moment from "moment";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";

Modal.setAppElement("body");

const STATUS_BADGE = {
  sent: "success",
  failed: "danger",
  pending: "warning",
};

export default function EmailLogs({ protectedFetch, customerEmail, ticketId }) {
  const [emailLogs, setEmailLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [bodyModalOpen, setBodyModalOpen] = useState(false);
  const [bodyLog, setBodyLog] = useState(null);
  const { showToast } = useToast();

  const fetchEmailLogs = useCallback(() => {
    setIsLoading(true);
    protectedFetch(
      apiEndpoints.ticket.getEmailLogs(ticketId),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => setEmailLogs(res || []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [protectedFetch]);

  useEffect(() => {
    fetchEmailLogs();
  }, [fetchEmailLogs]);

  function openResendModal(log) {
    setSelectedLog(log);
    setResendEmail(log.to_email || customerEmail || "");
    setResendModalOpen(true);
  }

  function closeResendModal() {
    setResendModalOpen(false);
    setSelectedLog(null);
    setResendEmail("");
    setIsSending(false);
  }

  function openBodyModal(log) {
    setBodyLog(log);
    setBodyModalOpen(true);
  }

  function closeBodyModal() {
    setBodyModalOpen(false);
    setBodyLog(null);
  }

  function handleResend() {
    if (!resendEmail) return;
    setIsSending(true);

    protectedFetch(
      apiEndpoints.ticket.resendEmailLogs(selectedLog.id),
      "POST",
      { email: resendEmail },
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        closeResendModal();
        fetchEmailLogs();
        showToast(res.detail || "Email resend successfully.", "success");
      })
      .catch((error) => {
        showToast(
          error?.response?.data?.detail ||
            error?.response?.data?.email?.[0] ||
            "Failed to resend email. Please try again.",
          "error",
        );
        setIsSending(false);
      });
  }

  if (isLoading) {
    return <div className="p-3 text-muted">Loading email logs...</div>;
  }

  if (!emailLogs.length) {
    return <div className="p-3 text-muted">No email logs found.</div>;
  }

  const sorted = [...emailLogs].sort(
    (a, b) => moment(b.created) - moment(a.created),
  );

  return (
    <div>
      <div className="activity-wrapper">
        <ul style={{ padding: "0px" }}>
          {sorted.map((log) => {
            const badgeVariant =
              STATUS_BADGE[log.status?.toLowerCase()] || "secondary";
            return (
              <li key={log.id} style={{ listStyle: "none" }}>
                <div className="card mb-2" data-testid={`email-log-${log.id}`}>
                  <div
                    style={{ padding: "3px 8px", margin: "1px" }}
                    className="card-header d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <strong>Sender:</strong> {log.sent_by || "—"}
                    </span>
                    <span
                      className={`badge badge-${badgeVariant}`}
                      style={{ textTransform: "capitalize" }}
                    >
                      {log.status || "unknown"}
                    </span>
                  </div>

                  <div className="card-body">
                    <div className="mb-1">
                      <strong>Subject:</strong> {log.subject || "—"}
                    </div>
                    <div className="mb-1">
                      <strong>To:</strong> {log.to_email || "—"}
                    </div>
                    <div>
                      <strong>Message:</strong>{" "}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => openBodyModal(log)}
                      >
                        View body
                      </button>
                    </div>
                  </div>

                  <div className="card-footer d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {log.created
                        ? moment(log.created).format("DD MMMM YYYY HH:mm")
                        : "—"}
                    </small>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => openResendModal(log)}
                    >
                      Resend
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <Modal
        isOpen={bodyModalOpen}
        contentLabel="Email Body"
        className="input-modal"
        style={{
          content: {
            width: "700px",
            maxWidth: "90vw",
            maxHeight: "80vh",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div
          className="card"
          style={{
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <button onClick={closeBodyModal} className="confirm-modal-close">
            <MdClose />
          </button>
          <div className="card-header">
            <div>
              <strong>Subject:</strong> {bodyLog?.subject || "—"}
            </div>
            <div>
              <strong>Sender:</strong> {bodyLog?.sent_by || "—"}
            </div>
            <div>
              <strong>To:</strong> {bodyLog?.to_email || "—"}
            </div>
          </div>
          <div
            className="card-body"
            style={{ overflow: "auto", flex: 1 }}
            dangerouslySetInnerHTML={{ __html: bodyLog?.body || "—" }}
          />
          <div className="card-footer text-right">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closeBodyModal}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={resendModalOpen}
        contentLabel="Resend Email"
        className="input-modal"
        style={{
          content: {
            width: "400px",
            height: "auto",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          },
        }}
      >
        <div className="card">
          <button onClick={closeResendModal} className="confirm-modal-close">
            <MdClose />
          </button>
          <div className="card-header">Resend Email</div>
          <div className="card-body">
            {/* <div className="form-group mb-0">
              <label htmlFor="resend-email-input">
                <strong>Recipient Email</strong>
              </label>
              <input
                id="resend-email-input"
                type="email"
                className="form-control mt-1"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Enter email address"
              />
            </div> */}
            <div>
              Are you sure you want to resend the email to {resendEmail}?
            </div>
          </div>
          <div className="card-footer">
            <button
              type="button"
              className="btn btn-outline-primary mr-3"
              onClick={handleResend}
              disabled={!resendEmail || isSending}
            >
              {isSending ? "Sending..." : "Send"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closeResendModal}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
